/**
 * CampaignTemplateSelector - Componente para seleção e configuração de template
 * 
 * Suporte completo a:
 * - Templates aprovados das inboxes selecionadas
 * - Preview estilo WhatsApp (header, body, footer, botões)
 * - Variáveis do BODY com valores fixos ou variáveis da lista
 * - URLs dinâmicas com sufixo variável
 * - ORDER_DETAILS (PIX) com todos campos suportando variáveis
 * 
 * @author Sistema de Campanhas v3.0
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Check, 
  FileText, 
  AlertCircle,
  ExternalLink,
  Phone,
  MessageSquare,
  CreditCard,
  QrCode,
  Image,
  Video,
  FileIcon,
  Globe,
  Search
} from 'lucide-react';
import { TemplateParams, OrderDetailsParams } from './types';
import { cn } from '@/lib/utils';

// =====================================================
// INTERFACES
// =====================================================

// Interface para template do WhatsApp
interface WhatsAppTemplate {
  id: string;
  inbox_id: string;
  name: string;
  language: string;
  status: string;
  category: string;
  components: Array<{
    type: string;
    format?: string;
    text?: string;
    example?: {
      body_text?: string[][];
      header_text?: string[];
    };
    buttons?: Array<{
      type: string;
      text?: string;
      url?: string;
      phone_number?: string;
      example?: string[];
    }>;
  }>;
}

interface CampaignTemplateSelectorProps {
  selectedInboxes: string[];
  selectedTemplate: WhatsAppTemplate | null;
  onSelectTemplate: (template: WhatsAppTemplate | null) => void;
  templateParams: TemplateParams;
  onTemplateParamsChange: (params: TemplateParams) => void;
  availableListVariables?: string[];
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function CampaignTemplateSelector({
  selectedInboxes,
  selectedTemplate,
  onSelectTemplate,
  templateParams,
  onTemplateParamsChange,
  availableListVariables = [],
}: CampaignTemplateSelectorProps) {
  // State para ORDER_DETAILS (PIX)
  const [orderDetailsParams, setOrderDetailsParams] = useState<OrderDetailsParams>({
    pix_code: '',
    merchant_name: '',
    pix_key: '',
    pix_key_type: 'CNPJ',
    amount_value: '',
    item_name: '',
    reference_id: '',
  });

  // State para pesquisa de templates
  const [templateSearch, setTemplateSearch] = useState('');

  // Buscar templates aprovados das inboxes selecionadas
  const { data: templates, isLoading } = useQuery({
    queryKey: ['campaign-templates', selectedInboxes],
    queryFn: async () => {
      if (selectedInboxes.length === 0) return [];
      
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .in('inbox_id', selectedInboxes)
        .eq('status', 'APPROVED')
        .order('name');

      if (error) throw error;
      return (data || []) as unknown as WhatsAppTemplate[];
    },
    enabled: selectedInboxes.length > 0,
  });

  // Variáveis padrão + variáveis da lista + variáveis PIX
  const allVariables = useMemo(() => [
    { value: '{{nome}}', label: 'Nome do contato', group: 'Sistema' },
    { value: '{{telefone}}', label: 'Telefone', group: 'Sistema' },
    { value: '{{email}}', label: 'E-mail', group: 'Sistema' },
    // Variáveis PIX do modelo oficial
    { value: '{{pix_code}}', label: 'Código PIX', group: 'PIX' },
    { value: '{{pix_merchant}}', label: 'Recebedor PIX', group: 'PIX' },
    { value: '{{pix_key}}', label: 'Chave PIX', group: 'PIX' },
    { value: '{{pix_key_type}}', label: 'Tipo Chave PIX', group: 'PIX' },
    { value: '{{pix_amount}}', label: 'Valor PIX', group: 'PIX' },
    { value: '{{pix_description}}', label: 'Descrição PIX', group: 'PIX' },
    { value: '{{url_suffix}}', label: 'Sufixo URL', group: 'URL' },
    // Variáveis personalizadas da lista
    ...availableListVariables.map(v => ({
      value: `{{${v}}}`,
      label: v,
      group: 'Lista'
    }))
  ], [availableListVariables]);

  // =====================================================
  // FUNÇÕES AUXILIARES - EXTRAÇÃO DE COMPONENTES
  // =====================================================

  const getBodyVariableCount = (template: WhatsAppTemplate): number => {
    const bodyComponent = template.components?.find(c => c.type === 'BODY');
    if (!bodyComponent?.text) return 0;
    const matches = bodyComponent.text.match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
  };

  const getHeaderVariableCount = (template: WhatsAppTemplate): number => {
    const headerComponent = template.components?.find(c => c.type === 'HEADER');
    if (!headerComponent?.text) return 0;
    const matches = headerComponent.text.match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
  };

  const getBodyText = (template: WhatsAppTemplate): string => {
    return template.components?.find(c => c.type === 'BODY')?.text || '';
  };

  // Filtrar templates baseado no termo de busca (após getBodyText ser declarado)
  const filteredTemplates = useMemo(() => {
    if (!templates || !templateSearch.trim()) return templates || [];
    
    const search = templateSearch.toLowerCase().trim();
    return templates.filter(template => 
      template.name.toLowerCase().includes(search) ||
      getBodyText(template).toLowerCase().includes(search)
    );
  }, [templates, templateSearch]);

  const getHeader = (template: WhatsAppTemplate) => {
    return template.components?.find(c => c.type === 'HEADER');
  };

  const getFooter = (template: WhatsAppTemplate) => {
    return template.components?.find(c => c.type === 'FOOTER');
  };

  const getButtons = (template: WhatsAppTemplate) => {
    return template.components?.find(c => c.type === 'BUTTONS')?.buttons || [];
  };

  // Detectar tipos de botões
  const getDynamicUrlButtons = (template: WhatsAppTemplate) => {
    return getButtons(template).filter(btn => btn.type === 'URL' && btn.url?.includes('{{'));
  };

  const getOrderDetailsButtons = (template: WhatsAppTemplate) => {
    return getButtons(template).filter(btn => btn.type === 'ORDER_DETAILS');
  };

  const hasOrderDetails = selectedTemplate ? getOrderDetailsButtons(selectedTemplate).length > 0 : false;
  const hasDynamicUrl = selectedTemplate ? getDynamicUrlButtons(selectedTemplate).length > 0 : false;

  // =====================================================
  // EFFECTS
  // =====================================================

  // Quando template muda, inicializar params
  useEffect(() => {
    if (selectedTemplate) {
      const bodyCount = getBodyVariableCount(selectedTemplate);
      const headerCount = getHeaderVariableCount(selectedTemplate);
      const dynamicUrls = getDynamicUrlButtons(selectedTemplate);
      
      // Inicializar button_params para URLs dinâmicas
      const buttonParams = dynamicUrls.map((btn, idx) => ({
        type: 'button' as const,
        sub_type: 'url' as const,
        index: idx,
        parameters: [{ type: 'text' as const, text: '' }]
      }));

      onTemplateParamsChange({
        body_params: Array(bodyCount).fill(''),
        header_params: Array(headerCount).fill(''),
        button_params: buttonParams.length > 0 ? buttonParams : undefined,
        order_details_params: hasOrderDetails ? orderDetailsParams : undefined,
      });
    }
  }, [selectedTemplate?.id]);

  // Sincronizar ORDER_DETAILS com templateParams
  useEffect(() => {
    if (hasOrderDetails && orderDetailsParams) {
      onTemplateParamsChange({
        ...templateParams,
        order_details_params: orderDetailsParams,
      });
    }
  }, [orderDetailsParams, hasOrderDetails]);

  // =====================================================
  // HANDLERS
  // =====================================================

  const updateBodyParam = (index: number, value: string) => {
    const newParams = [...(templateParams.body_params || [])];
    newParams[index] = value;
    onTemplateParamsChange({ ...templateParams, body_params: newParams });
  };

  const updateButtonParam = (index: number, value: string) => {
    const currentParams = templateParams.button_params || [];
    const newParams = [...currentParams];
    if (newParams[index]) {
      newParams[index] = {
        ...newParams[index],
        parameters: [{ type: 'text' as const, text: value }]
      };
    }
    onTemplateParamsChange({ ...templateParams, button_params: newParams });
  };

  const updateOrderDetails = (field: keyof OrderDetailsParams, value: string) => {
    const updated = { ...orderDetailsParams, [field]: value };
    setOrderDetailsParams(updated);
  };

  // =====================================================
  // RENDER HELPERS
  // =====================================================

  // Ícone do tipo de header
  const HeaderFormatIcon = ({ format }: { format: string }) => {
    switch (format) {
      case 'IMAGE': return <Image className="h-4 w-4" />;
      case 'VIDEO': return <Video className="h-4 w-4" />;
      case 'DOCUMENT': return <FileIcon className="h-4 w-4" />;
      default: return null;
    }
  };

  // Ícone do tipo de botão
  const ButtonTypeIcon = ({ type }: { type: string }) => {
    switch (type) {
      case 'URL': return <ExternalLink className="h-3.5 w-3.5" />;
      case 'PHONE_NUMBER': return <Phone className="h-3.5 w-3.5" />;
      case 'ORDER_DETAILS': return <CreditCard className="h-3.5 w-3.5" />;
      default: return <MessageSquare className="h-3.5 w-3.5" />;
    }
  };

  // Preview do body com variáveis substituídas
  const getPreviewBody = () => {
    if (!selectedTemplate) return '';
    let body = getBodyText(selectedTemplate);
    (templateParams.body_params || []).forEach((value, idx) => {
      body = body.replace(new RegExp(`\\{\\{${idx + 1}\\}\\}`, 'g'), value || `{{${idx + 1}}}`);
    });
    return body;
  };

  // Categoria cores
  const categoryColors: Record<string, string> = {
    MARKETING: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
    UTILITY: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    AUTHENTICATION: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  };

  // =====================================================
  // LOADING / EMPTY STATES
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-3" />
        Carregando templates...
      </div>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground border rounded-lg">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm font-medium">Nenhum template aprovado encontrado.</p>
        <p className="text-xs mt-1">Verifique se as inboxes têm templates aprovados no Meta.</p>
      </div>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="space-y-4">
      {/* Layout 2 colunas: Grid de templates + Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Coluna 1: Grid de Templates */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Selecione um template</Label>
          
          {/* Campo de pesquisa de templates */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar template..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          
          <ScrollArea className="h-[220px] border rounded-lg p-2">
            {/* Estado vazio quando não encontrar resultados */}
            {filteredTemplates.length === 0 && templateSearch && (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm py-8">
                <Search className="h-6 w-6 mb-2 opacity-50" />
                <p>Nenhum template encontrado para "{templateSearch}"</p>
              </div>
            )}
            
            <div className="space-y-2">
              {filteredTemplates.map(template => (
                <div
                  key={template.id}
                  className={cn(
                    "p-3 border rounded-lg cursor-pointer transition-all",
                    selectedTemplate?.id === template.id
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'hover:bg-muted/50 border-border'
                  )}
                  onClick={() => onSelectTemplate(template)}
                >
                  {/* Header do card */}
                  <div className="flex items-start gap-2">
                    <div className={cn(
                      "h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5",
                      selectedTemplate?.id === template.id 
                        ? 'border-primary bg-primary' 
                        : 'border-muted-foreground/30'
                    )}>
                      {selectedTemplate?.id === template.id && (
                        <Check className="h-3 w-3 text-primary-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{template.name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Badge variant="outline" className={cn("text-[10px] h-5", categoryColors[template.category])}>
                          {template.category}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] h-5">
                          <Globe className="h-2.5 w-2.5 mr-0.5" />
                          {template.language}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Preview truncado */}
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed">
                    {getBodyText(template).slice(0, 80)}...
                  </p>

                  {/* Indicadores de recursos especiais */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {getDynamicUrlButtons(template).length > 0 && (
                      <Badge variant="outline" className="text-[9px] h-4 bg-blue-500/10 text-blue-600 border-blue-500/20">
                        <ExternalLink className="h-2 w-2 mr-0.5" />
                        URL Dinâmica
                      </Badge>
                    )}
                    {getOrderDetailsButtons(template).length > 0 && (
                      <Badge variant="outline" className="text-[9px] h-4 bg-green-500/10 text-green-600 border-green-500/20">
                        <CreditCard className="h-2 w-2 mr-0.5" />
                        PIX
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Coluna 2: Preview WhatsApp */}
        {selectedTemplate && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Preview do WhatsApp</Label>
            <div className="rounded-xl bg-[#e5ddd5] p-4 h-[268px] overflow-y-auto">
              <div className="max-w-[260px] mx-auto">
                {/* Balão de mensagem */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {/* Header */}
                  {getHeader(selectedTemplate) && (
                    <div className="border-b">
                      {getHeader(selectedTemplate)?.format === 'TEXT' ? (
                        <div className="px-3 py-2 font-semibold text-sm bg-muted/30">
                          {getHeader(selectedTemplate)?.text}
                        </div>
                      ) : (
                        <div className="bg-muted/50 p-4 flex items-center justify-center">
                          <div className="flex flex-col items-center gap-1 text-muted-foreground">
                            <HeaderFormatIcon format={getHeader(selectedTemplate)?.format || ''} />
                            <span className="text-[10px]">{getHeader(selectedTemplate)?.format}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Body */}
                  <div className="px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed">
                    {getPreviewBody()}
                  </div>

                  {/* Footer */}
                  {getFooter(selectedTemplate) && (
                    <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-t bg-muted/20">
                      {getFooter(selectedTemplate)?.text}
                    </div>
                  )}

                  {/* Buttons */}
                  {getButtons(selectedTemplate).length > 0 && (
                    <div className="border-t">
                      {getButtons(selectedTemplate).map((btn, idx) => (
                        <div key={idx} className={cn(idx > 0 && "border-t")}>
                          <button className={cn(
                            "w-full px-3 py-2 text-sm font-medium flex items-center justify-center gap-2",
                            btn.type === 'ORDER_DETAILS' 
                              ? "text-green-600 hover:bg-green-50" 
                              : "text-blue-500 hover:bg-muted/50"
                          )}>
                            <ButtonTypeIcon type={btn.type} />
                            {btn.text}
                          </button>
                          {btn.type === 'URL' && btn.url?.includes('{{') && (
                            <div className="px-3 pb-1.5 text-[10px] text-muted-foreground text-center">
                              <Badge variant="secondary" className="text-[9px] bg-green-500/10 text-green-600">
                                Dinâmica
                              </Badge>
                            </div>
                          )}
                          {btn.type === 'ORDER_DETAILS' && (
                            <div className="px-3 pb-1.5 text-[10px] text-green-600 text-center flex items-center justify-center gap-1">
                              <QrCode className="h-2.5 w-2.5" />
                              Pagamento PIX
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Timestamp */}
                <div className="text-right mt-1">
                  <span className="text-[10px] text-muted-foreground">
                    {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Seção de Configuração de Variáveis */}
      {selectedTemplate && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Configurar Variáveis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Variáveis do BODY */}
            {getBodyVariableCount(selectedTemplate) > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Mensagem (BODY)
                  </Label>
                </div>
                <div className="space-y-2">
                  {Array.from({ length: getBodyVariableCount(selectedTemplate) }).map((_, idx) => (
                    <div key={idx} className="grid grid-cols-[50px_1fr_140px] gap-2 items-center">
                      <span className="text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded text-center">
                        {`{{${idx + 1}}}`}
                      </span>
                      <Input
                        placeholder="Valor fixo para todos..."
                        value={templateParams.body_params?.[idx]?.startsWith('{{') ? '' : (templateParams.body_params?.[idx] || '')}
                        onChange={(e) => updateBodyParam(idx, e.target.value)}
                        className="h-8 text-sm"
                      />
                      <Select
                        value={templateParams.body_params?.[idx]?.startsWith('{{') ? templateParams.body_params[idx] : ''}
                        onValueChange={(value) => updateBodyParam(idx, value)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Variável..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allVariables.map(v => (
                            <SelectItem key={v.value} value={v.value} className="text-xs">
                              <span className="flex items-center gap-2">
                                <Badge variant="outline" className="text-[9px] h-4">{v.group}</Badge>
                                {v.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* URLs Dinâmicas */}
            {hasDynamicUrl && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-blue-500" />
                    <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Botões com URL Dinâmica
                    </Label>
                  </div>
                  {getDynamicUrlButtons(selectedTemplate).map((btn, idx) => (
                    <div key={idx} className="p-3 bg-muted/30 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">{btn.text}</Badge>
                        <span className="text-xs text-muted-foreground">URL: {btn.url}</span>
                      </div>
                      <div className="grid grid-cols-[80px_1fr_140px] gap-2 items-center">
                        <span className="text-xs text-muted-foreground">Sufixo:</span>
                        <Input
                          placeholder="Valor fixo (ex: ABC123)..."
                          value={templateParams.button_params?.[idx]?.parameters?.[0]?.text?.startsWith('{{') ? '' : (templateParams.button_params?.[idx]?.parameters?.[0]?.text || '')}
                          onChange={(e) => updateButtonParam(idx, e.target.value)}
                          className="h-8 text-sm"
                        />
                        <Select
                          value={templateParams.button_params?.[idx]?.parameters?.[0]?.text?.startsWith('{{') ? templateParams.button_params[idx].parameters[0].text : ''}
                          onValueChange={(value) => updateButtonParam(idx, value)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Variável..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="{{url_suffix}}" className="text-xs">
                              <span className="flex items-center gap-2">
                                <Badge variant="outline" className="text-[9px] h-4">URL</Badge>
                                Sufixo URL
                              </span>
                            </SelectItem>
                            {availableListVariables.map(v => (
                              <SelectItem key={v} value={`{{${v}}}`} className="text-xs">
                                <span className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-[9px] h-4">Lista</Badge>
                                  {v}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ORDER_DETAILS (PIX) */}
            {hasOrderDetails && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-green-600" />
                    <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Pagamento PIX (ORDER_DETAILS)
                    </Label>
                  </div>
                  
                  <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg space-y-3">
                    {/* Código PIX */}
                    <PixFieldRow
                      label="Código PIX *"
                      value={orderDetailsParams.pix_code}
                      onChange={(v) => updateOrderDetails('pix_code', v)}
                      allVariables={allVariables}
                      placeholder="Cole o código PIX copia-cola completo..."
                      suggestedVariable="{{pix_code}}"
                    />

                    {/* Recebedor */}
                    <PixFieldRow
                      label="Recebedor *"
                      value={orderDetailsParams.merchant_name}
                      onChange={(v) => updateOrderDetails('merchant_name', v)}
                      allVariables={allVariables}
                      placeholder="Nome do beneficiário..."
                      suggestedVariable="{{pix_merchant}}"
                    />

                    {/* Chave PIX + Tipo */}
                    <div className="grid grid-cols-2 gap-2">
                      <PixFieldRow
                        label="Chave PIX *"
                        value={orderDetailsParams.pix_key}
                        onChange={(v) => updateOrderDetails('pix_key', v)}
                        allVariables={allVariables}
                        placeholder="CNPJ, CPF, email..."
                        suggestedVariable="{{pix_key}}"
                      />
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Tipo da Chave *</Label>
                        <Select
                          value={orderDetailsParams.pix_key_type}
                          onValueChange={(v) => updateOrderDetails('pix_key_type', v)}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CNPJ">CNPJ</SelectItem>
                            <SelectItem value="CPF">CPF</SelectItem>
                            <SelectItem value="EMAIL">Email</SelectItem>
                            <SelectItem value="CELULAR">Celular</SelectItem>
                            <SelectItem value="ALEATORIA">Chave Aleatória</SelectItem>
                            <SelectItem value="{{pix_key_type}}">
                              <span className="flex items-center gap-1">
                                <Badge variant="outline" className="text-[9px] h-4">Var</Badge>
                                pix_key_type
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Valor + Descrição */}
                    <div className="grid grid-cols-2 gap-2">
                      <PixFieldRow
                        label="Valor (R$) *"
                        value={orderDetailsParams.amount_value}
                        onChange={(v) => updateOrderDetails('amount_value', v)}
                        allVariables={allVariables}
                        placeholder="149.90"
                        suggestedVariable="{{pix_amount}}"
                      />
                      <PixFieldRow
                        label="Descrição"
                        value={orderDetailsParams.item_name || ''}
                        onChange={(v) => updateOrderDetails('item_name', v)}
                        allVariables={allVariables}
                        placeholder="Mensalidade, Produto..."
                        suggestedVariable="{{pix_description}}"
                      />
                    </div>

                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                      Use variáveis do arquivo CSV para personalizar cada pagamento, ou digite valores fixos.
                      Baixe o modelo de CSV para WhatsApp Oficial para ter as colunas corretas.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Dica final */}
            <p className="text-xs text-muted-foreground leading-relaxed">
              Use variáveis da lista para personalizar cada mensagem, ou digite um valor fixo para todos os destinatários.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =====================================================
// COMPONENTE AUXILIAR: Campo PIX com Input + Select
// =====================================================

interface PixFieldRowProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  allVariables: Array<{ value: string; label: string; group: string }>;
  placeholder: string;
  suggestedVariable: string;
}

function PixFieldRow({ label, value, onChange, allVariables, placeholder, suggestedVariable }: PixFieldRowProps) {
  const isVariable = value.startsWith('{{');
  
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="grid grid-cols-[1fr_120px] gap-1.5">
        <Input
          placeholder={placeholder}
          value={isVariable ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 text-sm"
        />
        <Select
          value={isVariable ? value : ''}
          onValueChange={onChange}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Variável" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={suggestedVariable} className="text-xs font-medium">
              {suggestedVariable}
            </SelectItem>
            <Separator className="my-1" />
            {allVariables.filter(v => v.group !== 'PIX').map(v => (
              <SelectItem key={v.value} value={v.value} className="text-xs">
                <span className="flex items-center gap-1">
                  <Badge variant="outline" className="text-[9px] h-4">{v.group}</Badge>
                  {v.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
